import { useState, useEffect } from "react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { CalendarIcon, Loader2, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import type { Tables } from "@/integrations/supabase/types";
import { TYPE_CONFIG, CATEGORY_CONFIG, getTypeLabel, getCategoryLabel } from "@/constants/ui-mapping";

type Document = Tables<"documents">;

interface DocumentEditProps {
  document: Document | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave?: () => void;
}

export function DocumentEdit({ document, open, onOpenChange, onSave }: DocumentEditProps) {
  const [title, setTitle] = useState(document?.title || "");
  const [docType, setDocType] = useState<string>(document?.doc_type || "other");
  const [category, setCategory] = useState<string>(document?.category || "other");
  const [expiresAt, setExpiresAt] = useState<Date | undefined>(
    document?.expires_at ? new Date(document.expires_at) : undefined
  );
  const [isPrivate, setIsPrivate] = useState<boolean>(document?.is_private ?? true);
  const [hasOrganization, setHasOrganization] = useState(false);
  const [saving, setSaving] = useState(false);

  // Check if user belongs to an organization
  useEffect(() => {
    const checkOrganization = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("organization_id")
          .eq("id", user.id)
          .single();
        setHasOrganization(!!profile?.organization_id);
      }
    };
    checkOrganization();
  }, []);

  // Reset form when document changes
  useEffect(() => {
    if (document) {
      setTitle(document.title);
      setDocType(document.doc_type);
      setCategory(document.category || "other");
      setExpiresAt(document.expires_at ? new Date(document.expires_at) : undefined);
      setIsPrivate(document.is_private ?? true);
    }
  }, [document]);

  // Save user corrections for AI feedback loop
  const saveCorrections = async (documentId: string, originalDoc: Document) => {
    const corrections: { field_name: string; original_value: string | null; corrected_value: string }[] = [];
    
    if (title !== originalDoc.title) {
      corrections.push({
        field_name: "title",
        original_value: originalDoc.title,
        corrected_value: title
      });
    }
    
    if (docType !== originalDoc.doc_type) {
      corrections.push({
        field_name: "doc_type",
        original_value: originalDoc.doc_type,
        corrected_value: docType
      });
    }

    if (category !== (originalDoc.category || "other")) {
      corrections.push({
        field_name: "category",
        original_value: originalDoc.category || "other",
        corrected_value: category
      });
    }
    
    const originalExpires = originalDoc.expires_at ? format(new Date(originalDoc.expires_at), "yyyy-MM-dd") : null;
    const newExpires = expiresAt ? format(expiresAt, "yyyy-MM-dd") : null;
    if (newExpires !== originalExpires) {
      corrections.push({
        field_name: "expires_at",
        original_value: originalExpires,
        corrected_value: newExpires || "null"
      });
    }
    
    if (corrections.length > 0) {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { error } = await supabase.from("document_corrections").insert(
          corrections.map(c => ({
            document_id: documentId,
            user_id: user.id,
            field_name: c.field_name,
            original_value: c.original_value,
            corrected_value: c.corrected_value
          }))
        );
        if (error) {
          console.error("Error saving corrections:", error);
        }
      }
    }
  };

  const handleSave = async () => {
    if (!document) return;

    setSaving(true);
    
    // Save corrections for AI feedback loop before updating
    await saveCorrections(document.id, document);
    
    const { error } = await supabase
      .from("documents")
      .update({
        title,
        doc_type: docType as any,
        category: category as any,
        expires_at: expiresAt ? format(expiresAt, "yyyy-MM-dd") : null,
        is_private: isPrivate,
      })
      .eq("id", document.id);

    if (error) {
      console.error("Error updating document:", error);
      toast.error("Erreur lors de la mise à jour");
    } else {
      toast.success("Document mis à jour");
      onSave?.();
      onOpenChange(false);
    }
    setSaving(false);
  };

  const clearExpiration = () => {
    setExpiresAt(undefined);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Modifier le document</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Titre</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Titre du document"
            />
          </div>

          {/* Document Type */}
          <div className="space-y-2">
            <Label>Type de document</Label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner un type">
                  {docType && getTypeLabel(docType)}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="bg-background z-50">
                {Object.entries(TYPE_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key}>
                    {config.emoji} {config.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Category */}
          <div className="space-y-2">
            <Label>Catégorie</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner une catégorie">
                  {category && getCategoryLabel(category)}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="bg-background z-50">
                {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key}>
                    {config.emoji} {config.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Organization Sharing Toggle - only show if user belongs to an org */}
          {hasOrganization && (
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div className="space-y-0.5">
                <Label htmlFor="share-org" className="flex items-center gap-2 cursor-pointer">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  Partager avec l'organisation
                </Label>
                <p className="text-xs text-muted-foreground">
                  Les membres de votre organisation pourront voir ce document
                </p>
              </div>
              <Switch
                id="share-org"
                checked={!isPrivate}
                onCheckedChange={(checked) => setIsPrivate(!checked)}
              />
            </div>
          )}

          {/* Expiration Date */}
          <div className="space-y-2">
            <Label>Date d'expiration</Label>
            <div className="flex gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "flex-1 justify-start text-left font-normal",
                      !expiresAt && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {expiresAt ? format(expiresAt, "d MMMM yyyy", { locale: fr }) : "Aucune date définie"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={expiresAt}
                    onSelect={setExpiresAt}
                    initialFocus
                    locale={fr}
                    className="p-3 pointer-events-auto"
                    disabled={(date) => date < new Date()}
                  />
                </PopoverContent>
              </Popover>
              {expiresAt && (
                <Button variant="ghost" size="icon" onClick={clearExpiration}>
                  ×
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Vous recevrez une notification avant l'expiration du document
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Annuler
          </Button>
          <Button onClick={handleSave} disabled={saving || !title.trim()}>
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Enregistrement...
              </>
            ) : (
              "Enregistrer"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
